import { LoadingOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

import InputGroup from 'components/Views/Creative/DynamicProperties/Inputs/InputGroup';
import { maybeAddChangeListener } from 'stores/DynamicProperties/changeListeners';
import type DynamicPropertiesManager from 'stores/DynamicProperties/Manager';
import store from 'stores/RootStore';
import { DynamicGroupEvent } from 'types/DynamicGroupEvent';
import { DynamicPropertyStatus } from 'types/DynamicPropertyStatus';
import { PanelViews } from 'types/PanelViews';
import {
  ifPropertyManagerType,
  PropertyManagerType,
} from 'types/PropertyManagerType';
import { useForceUpdate } from 'utils/Hooks';

import DrawerLayout, { type DrawerLayoutController } from '../DrawerLayout';
import PropertiesDrawerToolbar from './PropertiesDrawerToolbar';

const RootGroup = styled(InputGroup)`
  border: none;
  margin-top: 0px;
  margin-bottom: 0px;
`;

const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 65px);
  width: 100%;
`;

const DisabledOverlay = styled.div`
  position: absolute;
  top: 65px;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const LoadingIcon = styled(LoadingOutlined)`
  font-size: 24px;
`;

const StatusMessageContainer = styled.div`
  margin-top: 0.75em;
`;

function selectScopedProperties(
  tabMode: string | undefined,
  adProperties: DynamicPropertiesManager | undefined,
  templateProperties: DynamicPropertiesManager | undefined,
  creativeProperties: DynamicPropertiesManager | undefined
): DynamicPropertiesManager | undefined {
  switch (tabMode) {
    case PropertyManagerType.AD:
      return adProperties;
    case PropertyManagerType.TEMPLATE:
      return templateProperties;
    case PropertyManagerType.CREATIVE:
      return creativeProperties;
  }
  return undefined;
}

function PropertiesDrawer() {
  const store_creative = store.creative;

  const store_creative_loading = store_creative?.loading;
  const currentPropertyManager = store_creative?.currentPropertyManager;
  const store_creative_dynamicProperties = store_creative?.dynamicProperties;
  const currentAd = store_creative?.ads.current;
  const currentAd_properties = currentAd?.properties;
  const currentAd_template_properties = currentAd?.getTemplate()?.properties;
  const displayPanel = store_creative?.displayPanel;

  const handleOnClose = () => {
    store_creative?.setPanel(PanelViews.NONE);
  };

  const handleTabChange = (tabMode: PropertyManagerType) => {
    store_creative?.setCurrentPropertyManager(tabMode);
  };

  const drawerLayoutRef = useRef<DrawerLayoutController>(null);

  useEffect(() => {
    if (!store_creative_dynamicProperties) return;

    const onPropertyAdded = () => {
      drawerLayoutRef.current?.scrollToBottom();
    };

    store_creative_dynamicProperties.addEventListener(
      DynamicGroupEvent.ADDED,
      onPropertyAdded
    );

    return () => {
      store_creative_dynamicProperties.removeEventListener(
        DynamicGroupEvent.ADDED,
        onPropertyAdded
      );
    };
  }, [store_creative_dynamicProperties]);

  const forceUpdate = useForceUpdate();
  useEffect(
    () => maybeAddChangeListener(store_creative_dynamicProperties, forceUpdate),
    [store_creative_dynamicProperties, forceUpdate]
  );

  useEffect(
    () => maybeAddChangeListener(currentAd_properties, forceUpdate),
    [currentAd_properties, forceUpdate]
  );

  useEffect(
    () => maybeAddChangeListener(currentAd_template_properties, forceUpdate),
    [currentAd_template_properties, forceUpdate]
  );

  const tabMode = ifPropertyManagerType(currentPropertyManager?.name);
  const selectedScopedProperties = selectScopedProperties(
    tabMode,
    currentAd_properties,
    // Was reached a second way here - `properties.get('template').store` -
    // which resolves the template from the *live* property tree, while
    // `Ad.template` above resolves it from the persisted `data.dynamicData`.
    // Those disagree between editing an ad's template and saving it, so the
    // effect above was subscribing to one template's properties while this
    // rendered the other's. Creative.setCurrentPropertyManager also uses
    // `Ad.template`, so Save and Revert already acted on this one.
    //
    // `Ad.template` is also the only reactive route: Ad excludes `properties`
    // from becomeObservable but `data` is observable, so the live tree only
    // ever notifies through its CHANGED event.
    currentAd_template_properties,
    store_creative_dynamicProperties
  );
  const editMode = tabMode === PropertyManagerType.CREATIVE;

  const isLoading =
    currentPropertyManager?.status === DynamicPropertyStatus.LOADING;
  const isContentLoading = isLoading || store_creative_loading;

  return (
    <DrawerLayout
      ref={drawerLayoutRef}
      open={displayPanel === PanelViews.AD}
      onClose={handleOnClose}
      title={
        <PropertiesDrawerToolbar
          tabMode={tabMode}
          currentPropertiesManager={currentPropertyManager}
          currentAd={currentAd}
          handleTabChange={handleTabChange}
        />
      }
    >
      <ContentContainer>
        {tabMode && (
          <RootGroup
            key={
              /* This forces a teardown and rebuild of RootGroup when tabMode
                 changes. Not sure if it's necessary, just reproduces the
                 lifecycles of the older code which did
                 `tabMode === PropertyManagerType.SOMEVALUE && <RootGroup ...>`
                 in triplicate
              */
              tabMode
            }
            hideLabel={true}
            property={selectedScopedProperties}
            disabled={isContentLoading}
            editMode={editMode}
          />
        )}

        {isLoading && (
          <DisabledOverlay className="DisabledOverlay">
            <LoadingContainer className="LoadingContainer">
              <LoadingIcon />
              <StatusMessageContainer>
                {currentPropertyManager?.statusMessage}
              </StatusMessageContainer>
            </LoadingContainer>
          </DisabledOverlay>
        )}
      </ContentContainer>
    </DrawerLayout>
  );
}

export default observer(PropertiesDrawer);
